import axios from "axios";
import moment from "moment";

import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@material-ui/core";
import { useEffect, useState } from "react";
import DataTable from "../components/DataTable";
import { DataGrid } from "@material-ui/data-grid";

const MissingSimRacks = ({ setLoading, setMessage, setError, loading }) => {
  const [lastUpdatedMissing, setLastUpdatedMissing] = useState(undefined);
  const [missingSimRacks, setMissingSimRacks] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [openUpdateNoteDialog, setUpdateNoteDialog] = useState(false);
  const [filterBrokenData, setFilterBrokenData] = useState(true);
  const [filteredData, setFilteredData] = useState([]);

  const getMissingSimRacks = () => {
    setLoading(true);
    axios
      .get("/sim-rack/missing-racks")
      .then(({ data }) => {
        setMissingSimRacks(data.missing_sim_racks);
        setLastUpdatedMissing(data.last_updated_missing);
      })
      .catch((error) => {
        setError(error.response.data.detail);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleNoteUpdate = (note) => {
    setLoading(true);
    axios
      .post(`/sim-rack/missing-racks/update-note`, {
        note: note,
        update_ids: selectedIds,
      })
      .then((_) => {
        setMessage("Note updated successfully");
        setSelectedIds([]);
      })
      .catch((error) => {
        setError(error.response.data.detail);
      })
      .finally(() => {
        setLoading(false);
        setUpdateNoteDialog(false);
        getMissingSimRacks();
      });
  };

  const triggerMissingCheck = () => {
    setLoading(true);
    axios
      .post(`/sim-rack/missing-racks/trigger`)
      .then((_) => {
        setMessage("Missing racks check triggered successfully");
      })
      .catch((error) => {
        setError(error.response.data.detail);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const downloadExportData = () => {
    setLoading(true);
    axios.get(`/sim-rack/missing-racks/export`).then(({ data }) => {
      const url = window.URL.createObjectURL(
        new Blob([data], { type: "text/csv" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `missing_sim_racks${moment().format("YYYYMMDDhmmss")}.csv`
      );
      document.body.appendChild(link);
      link.click();
      setLoading(false);
    });
  };

  useEffect(() => {
    getMissingSimRacks();
  }, []);

  const columns = [
    {
      field: "sim_rack_name",
      headerName: "Sim Rack Name",
      minWidth: 200,
      filterable: true,
      flex: 2,
    },
    {
      field: "tray",
      headerName: "Tray",
      filterable: true,
      flex: 1,
    },
    {
      field: "slot",
      headerName: "Slot",
      filterable: true,
      flex: 1,
    },
    { field: "note", headerName: "Note", filterable: true, flex: 2 },
    {
      field: "last_updated_at",
      headerName: "Last Updated At",
      filterable: true,
      minWidth: 160,
      valueGetter: (params) =>
        params.row.last_updated_at &&
        moment(params.row.last_updated_at + "Z")
          .local()
          .format("LLL"),
      flex: 2,
    },
  ];

  useEffect(() => {
    if (filterBrokenData) {
      setFilteredData(
        missingSimRacks.filter((item) =>
          !item?.note?.toLowerCase().includes("broken")
        )
      );
    } else {
      setFilteredData(missingSimRacks);
    }
  }, [missingSimRacks, filterBrokenData]);

  const UpdateNoteDialog = (_) => {
    const [value, setValue] = useState("");
    return (
      <Dialog
        open={openUpdateNoteDialog}
        onClose={() => setUpdateNoteDialog(null)}
        aria-labelledby="form-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="update-dialog-title">Update</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Add notes:</Typography>
          <TextField
            label="Updated Value"
            fullWidth
            value={value}
            onChange={(e) => setValue(e.target.value)}
            variant="outlined"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            disabled={loading}
            onClick={() => handleNoteUpdate(value)}
          >
            Update
          </Button>
          <Button onClick={() => setUpdateNoteDialog(null)} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <>
      <UpdateNoteDialog />
      <Grid container spacing={2} direction="row" justifyContent="flex-start">
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            disabled={loading || selectedIds.length === 0}
            onClick={() => setUpdateNoteDialog(true)}
          >
            Add Note
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            onClick={downloadExportData}
          >
            Export Missing Racks
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            color="secondary"
            onClick={triggerMissingCheck}
          >
            Trigger Missing Check{" "}
            {lastUpdatedMissing
              ? `(Last Updated: ${moment(lastUpdatedMissing + "Z")
                  .local()
                  .format("LLL")})`
              : ""}
          </Button>
        </Grid>
      </Grid>
      <FormControlLabel
        control={
          <Checkbox
            checked={filterBrokenData}
            onChange={(e) => setFilterBrokenData(e.target.checked)}
            inputProps={{ "aria-label": "primary checkbox" }}
            color="primary"
          />
        }
        label="Filter Broken"
      />
      <DataGrid
        rows={filteredData}
        columns={columns}
        autoHeight
        paginationMode="client"
        loading={loading}
        getRowId={(row) => row.id}
        density="compact"
        checkboxSelection={true}
        selectionModel={selectedIds}
        onSelectionModelChange={setSelectedIds}
      />
    </>
  );
};

export default MissingSimRacks;
